import {
  buildUseMutation,
  ErrorAlert,
  Form,
  FormItemWrapper,
  multipleValidators,
  Skeleton,
  useForm,
  useQueryParams,
  useRouter,
  Validators,
} from '@main/core-ui';
import {
  Button,
  ButtonWrapper,
  FormFieldsWrapper,
  PcFormInput,
  SuccessWrapper,
} from '@main/pc-core-components';
import { endpoints } from '@main/pc-types';
import { RequestAction } from '@transcend-io/privacy-types';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { selectActiveDataSubject } from '../ActiveDataSubject/selectors';
import { sessionManagerMessages } from './messages';

const usePrivacyCenterEmailLogin = buildUseMutation(
  endpoints.privacyCenterEmailLogin,
);

/**
 * Form values
 */
export interface EmailLoginValues {
  /** The email address to send to */
  email: string;
}

/**
 * Login to the privacy center by sending an email to the data subject.
 */
export const EmailLogin: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [privacyCenterEmailLogin, { data, loading, error }] =
    usePrivacyCenterEmailLogin();
  const { formatMessage } = useIntl();
  const queryParams = useQueryParams();
  const { location } = useRouter();
  const dataSubject = useSelector(selectActiveDataSubject);
  const formMethods = useForm<EmailLoginValues>({
    defaultValues: {
      email: '',
    },
  });

  if (!dataSubject) {
    return <Skeleton />;
  }

  return (
    <Form
      useFormMethods={formMethods}
      onSubmit={(values: EmailLoginValues) => {
        privacyCenterEmailLogin({
          variables: {
            input: {
              action: queryParams.action as RequestAction,
              ...values,
              dataSubjectId: dataSubject.id,
              path: location.pathname,
            },
          },
        });
      }}
    >
      <FormFieldsWrapper>
        <Controller
          name="email"
          control={formMethods.control}
          rules={{
            validate: multipleValidators([
              Validators.REQUIRED,
              Validators.EMAIL,
            ]),
          }}
          render={({ field }) => (
            <FormItemWrapper
              label={formatMessage(sessionManagerMessages.email)}
              name="email"
              id="pc-email-login-input"
              errors={formMethods.formState.errors}
            >
              <PcFormInput
                id="pc-email-login-input"
                type="email"
                autoComplete="email"
                aria-describedby="form-input-validation-errors"
                {...field}
              />
            </FormItemWrapper>
          )}
        />
        {error && (
          <ErrorAlert errors={[error, sessionManagerMessages.adblockError]} />
        )}
      </FormFieldsWrapper>

      {data ? (
        <SuccessWrapper role="alert">
          {formatMessage(sessionManagerMessages.success)}
        </SuccessWrapper>
      ) : (
        <ButtonWrapper>
          <Button
            buttonType="primary"
            id="email-login-submit"
            type="submit"
            role="alert"
            loading={loading}
            disabled={!!loading || !!formMethods.formState.errors.email}
          >
            {formatMessage(sessionManagerMessages.submit)}
          </Button>
        </ButtonWrapper>
      )}
    </Form>
  );
};

export default EmailLogin;
